import { useState, useEffect, type ReactNode, type ReactElement } from 'react'
import { Flex } from '@magnetic/flex'
import { Card } from '@magnetic/card'
import { Text } from '@magnetic/text'
import { Button } from '@magnetic/button'
import { FilterBar } from '@magnetic/filter-bar'
import { Search } from '@magnetic/search'
import { Spinner } from '@magnetic/spinner'
import { Tooltip } from '@magnetic/tooltip'
import { Select } from '@magnetic/select'
import { Table, useMagneticTable, getPaginationRowModel, getSelectionColumn, type MdsTableColumn } from '@magnetic/table'
import { useQuery as useConnectQuery } from '@connectrpc/connect-query'
import { Database, Package, Warning, ArrowSquareOut, ArrowsClockwise, Positive, Negative } from '@/lib/icons'
import { bigIntToNumber } from '@/lib/protobuf-utils'
import type { ListRepositoriesResponse } from '@/gen/catpocs/v1/ecr_admin_pb'
import type { listECRRepositories } from '@/lib/api'

interface RepositoryInfo {
  name?: string
  uri?: string
  createdAt?: string
  imageCount?: number
}

interface RepositoryMetrics {
  totalImages?: number
  totalSizeBytes?: bigint | number
  imagesWithScanFindings?: number
  criticalFindings?: number
  highFindings?: number
  lastPushedAt?: string
}

interface RepositoryWithMetrics {
  repository?: RepositoryInfo
  metrics?: RepositoryMetrics
}

interface RepositoryRow extends RepositoryWithMetrics {
  id: string
}

interface ECRRepositoriesTableProps {
  // RPC query function to use
  queryFn: typeof listECRRepositories
  // Optional title
  title?: string
  // Optional description
  description?: string
  // Enable row selection
  selectableRows?: boolean
  // Custom configuration components (buttons) for the FilterBar
  configComponents?: ReactElement | null
  // Custom bulk actions for selected rows
  bulkActions?: (selectedCount: number, selectedRows: RepositoryRow[]) => ReactNode
  // Initial page size
  initialPageSize?: number
  // Refresh callback
  onRefresh?: () => void
}

function formatBytes(bytes: number | bigint | undefined): string {
  if (!bytes) return '0 B'
  const b = typeof bytes === 'bigint' ? Number(bytes) : bytes
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

function formatDate(timestamp: string | undefined): string {
  if (!timestamp) return 'Never'
  const date = new Date(timestamp)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function ECRRepositoriesTable({
  queryFn,
  title = 'ECR Repositories',
  description,
  selectableRows = false,
  configComponents,
  bulkActions,
  initialPageSize = 20,
  onRefresh,
}: ECRRepositoriesTableProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [securityFilter, setSecurityFilter] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [showReset, setShowReset] = useState(false)

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery)
      setCurrentPage(1) // Reset to first page on search
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // Show/hide reset button
  useEffect(() => {
    setShowReset(debouncedSearch !== '' || securityFilter !== '')
  }, [debouncedSearch, securityFilter])

  // Calculate pagination parameters
  const pageSize = initialPageSize
  const skip = (currentPage - 1) * pageSize

  // Fetch data with pagination and search
  const { data: rawData, isLoading, error, refetch } = useConnectQuery(
    queryFn,
    {
      pageSize: pageSize,
      skip: skip,
      search: debouncedSearch,
      securityFilter: securityFilter,
    },
    {
      refetchInterval: 60000, // Refresh every minute
    }
  )

  const response = rawData as ListRepositoriesResponse | undefined
  const repositories = (response?.repositories ?? []) as RepositoryWithMetrics[]
  const totalCount = response?.totalCount ?? 0

  // Transform repositories for table (add unique id)
  const repositoriesData: RepositoryRow[] = repositories.map((repo, index) => ({
    ...repo,
    id: repo.repository?.name || `repo-${index}`,
  }))

  const repositoryColumns: MdsTableColumn<RepositoryRow>[] = [
    {
      id: 'name',
      header: 'Repository',
      accessorFn: (row) => row.repository?.name || 'Unknown',
      cell: ({ getValue, row }) => (
        <Flex direction="vertical" gap={4}>
          <Text size="sm" weight="semi-bold">
            {getValue() as string}
          </Text>
          {row.original.repository?.uri && (
            <a
              href={row.original.repository.uri}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                fontSize: '0.75rem',
                opacity: 0.7,
                textDecoration: 'none',
              }}
            >
              View in AWS
              <ArrowSquareOut size={12} />
            </a>
          )}
        </Flex>
      ),
    },
    {
      id: 'images',
      header: 'Images',
      accessorFn: (row) => row.metrics?.totalImages || 0,
      cell: ({ getValue }) => (
        <Flex align="center" justify="flex-end" gap="xs">
          <Package size={16} style={{ opacity: 0.5 }} />
          <Text size="sm">{getValue() as number}</Text>
        </Flex>
      ),
    },
    {
      id: 'size',
      header: 'Size',
      accessorFn: (row) => bigIntToNumber(row.metrics?.totalSizeBytes),
      cell: ({ getValue }) => <Text size="sm">{formatBytes(getValue() as number)}</Text>,
    },
    {
      id: 'lastPush',
      header: 'Last Push',
      accessorFn: (row) => row.metrics?.lastPushedAt || '',
      cell: ({ getValue }) => (
        <Text size="sm" color="light">
          {formatDate(getValue() as string)}
        </Text>
      ),
    },
    {
      id: 'security',
      header: 'Security',
      accessorFn: (row) => row.metrics?.imagesWithScanFindings || 0,
      cell: ({ row }) => {
        const metrics = row.original.metrics
        const hasVulnerabilities = (metrics?.imagesWithScanFindings || 0) > 0

        if (hasVulnerabilities) {
          return (
            <Flex align="center" justify="center" gap="sm">
              {(metrics?.criticalFindings || 0) > 0 && (
                <Flex align="center" gap={4}>
                  <Negative />
                  <Text size="sm">{metrics?.criticalFindings} C</Text>
                </Flex>
              )}
              {(metrics?.highFindings || 0) > 0 && (
                <Flex align="center" gap={4}>
                  <Warning />
                  <Text size="sm">{metrics?.highFindings} H</Text>
                </Flex>
              )}
            </Flex>
          )
        }

        return (
          <Flex align="center" justify="center" gap={4}>
            <Positive />
            <Text size="sm">Clean</Text>
          </Flex>
        )
      },
    },
  ]

  // Add selection column if enabled
  const tableColumns: MdsTableColumn<RepositoryRow>[] = selectableRows
    ? [getSelectionColumn<RepositoryRow>(), ...repositoryColumns]
    : repositoryColumns

  const { table } = useMagneticTable({
    data: repositoriesData,
    columns: tableColumns,
    enableRowSelection: selectableRows,
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    pageCount: Math.ceil(totalCount / pageSize),
    state: {
      pagination: {
        pageIndex: currentPage - 1,
        pageSize: pageSize,
      },
    },
  })

  const selectedRows = selectableRows
    ? table.getSelectedRowModel().rows.map((row) => row.original)
    : []
  const selectedCount = selectedRows.length

  const handleReset = () => {
    setSearchQuery('')
    setDebouncedSearch('')
    setSecurityFilter('')
    setCurrentPage(1)
  }

  const handleRefresh = () => {
    refetch()
    if (onRefresh) {
      onRefresh()
    }
  }

  return (
    <Flex direction="vertical" gap={16}>
      {(title || description) && (
        <Flex direction="vertical" gap={8}>
          {title && <Text size="h3">{title}</Text>}
          {description && <Text size="p2">{description}</Text>}
        </Flex>
      )}

      <Card>
        <Flex direction="vertical" gap={16}>
          <Flex align="center" justify="space-between">
            <Flex align="center" gap="sm">
              <Database size={20} />
              <Text size="h4">{title}</Text>
            </Flex>
            <Tooltip title="Refresh repositories">
              <Button kind="tertiary" size="sm" onClick={handleRefresh} icon={<ArrowsClockwise size={16} />} />
            </Tooltip>
          </Flex>

          <FilterBar
            searchBar={
              <Search
                fixedWidth
                label="Search repositories"
                placeholder="Search by repository name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClear={searchQuery ? () => setSearchQuery('') : undefined}
                size="md"
              />
            }
            results={totalCount}
            isLoadingResults={isLoading}
            showResetButton={showReset}
            onReset={handleReset}
            configComponents={configComponents ?? undefined}
          >
            <Flex style={{ minWidth: '150px' }}>
              <Select
                placeholder="Filter by security"
                value={securityFilter}
                onChange={(value) => setSecurityFilter(value as string)}
                options={[
                  { value: '', label: 'All Repositories' },
                  { value: 'clean', label: 'Clean' },
                  { value: 'vulnerable', label: 'Vulnerable' },
                ]}
              />
            </Flex>
          </FilterBar>

          {isLoading && (
            <Flex align="center" justify="center" style={{ padding: '32px' }}>
              <Spinner size="lg" />
            </Flex>
          )}

          {error && (
            <Flex direction="vertical" align="center" gap={16} style={{ padding: '32px' }}>
              <Warning size={32} />
              <Text>Failed to load repositories</Text>
              <Text size="sm" color="light">
                {error instanceof Error ? error.message : 'Unknown error'}
              </Text>
              <Button kind="secondary" size="sm" onClick={handleRefresh} icon={<ArrowsClockwise size={16} />}>
                Retry
              </Button>
            </Flex>
          )}

          {!isLoading && !error && repositoriesData.length === 0 && (
            <Flex direction="vertical" align="center" gap={8} style={{ padding: '32px', opacity: 0.5 }}>
              <Database size={32} />
              <Text>No repositories found</Text>
            </Flex>
          )}

          {!isLoading && !error && repositoriesData.length > 0 && (
            <Table
              table={table}
              selectableRows={selectableRows}
              paginationControls={totalCount > pageSize ? {} : undefined}
            >
              {selectableRows && bulkActions && (
                <Table.BulkActionBar>
                  <Flex direction="horizontal" gap={8} align="center">
                    {bulkActions(selectedCount, selectedRows)}
                  </Flex>
                </Table.BulkActionBar>
              )}
            </Table>
          )}
        </Flex>
      </Card>
    </Flex>
  )
}
